// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module CohortTable
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS cohort-retention table for comparing
 * cohort sizes, retention checkpoints, and last activity across segments.
 */

import type { CohortAnalysis, CohortBucket } from "@geenius/analytics";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/react-css";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the React CSS cohort retention table.
 *
 * @property analysis Cohort analysis payload produced by shared helpers.
 * @property title Optional section title override.
 * @property maxRows Maximum cohort rows to render.
 * @property isLoading Whether the table should render placeholder rows.
 * @property error Optional error state rendered with retry recovery.
 * @property onRetry Optional retry callback for error states.
 * @property className Optional class name merged into the card wrapper.
 */
export interface CohortTableProps {
  analysis: CohortAnalysis | null;
  title?: string;
  maxRows?: number;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

function formatPercent(value: number | undefined): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

function getRetentionPoint(cohort: CohortBucket, day: number) {
  return cohort.retained.find((point) => point.day === day);
}

/**
 * Renders a cohort retention table for the React CSS analytics variant.
 *
 * @param props Cohort analysis data and optional loading/error controls.
 * @returns A card-framed cohort table with accessible row and column labels.
 */
export function CohortTable({
  analysis,
  className,
  error,
  isLoading,
  maxRows = 6,
  onRetry,
  title,
}: CohortTableProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const intervals = analysis?.intervals ?? [];
  const cohorts = (analysis?.cohorts ?? []).slice(0, maxRows);
  const tableTitle =
    title ?? analysis?.name ?? copy.t("analytics.dashboard.cohorts");

  return (
    <Card
      className={cx("gn-analytics-cohort-table", className)}
      data-analytics-cohort-table
    >
      <CardHeader>
        <CardTitle
          className="gn-analytics-cohort-table__title"
          data-analytics-section-title
        >
          {tableTitle}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error ? (
          <ErrorState error={error} onRetry={onRetry} />
        ) : isLoading ? (
          <div
            className="gn-analytics-cohort-table__loading"
            data-analytics-cohort-table-loading
          >
            <Skeleton height={24} />
            <Skeleton height={24} />
            <Skeleton height={24} />
          </div>
        ) : cohorts.length === 0 ? (
          <EmptyState
            title={copy.fallback(
              "analytics.dashboard.noCohorts",
              "No cohort data yet.",
            )}
          />
        ) : (
          <div className="gn-analytics-cohort-table__viewport">
            <table
              className="gn-analytics-cohort-table__table"
              data-analytics-table
              aria-label={tableTitle}
            >
              <thead>
                <tr>
                  <th scope="col">
                    {copy.fallback(
                      "analytics.cohorts.columns.cohort",
                      "Cohort",
                    )}
                  </th>
                  <th scope="col">
                    {copy.fallback("analytics.cohorts.columns.size", "Size")}
                  </th>
                  {intervals.map((day) => (
                    <th key={day} scope="col">
                      {copy.t("analytics.dashboardBuilder.dayLabel", { day })}
                    </th>
                  ))}
                  <th scope="col">
                    {copy.fallback(
                      "analytics.cohorts.columns.lastActivity",
                      "Last activity",
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {cohorts.map((cohort) => (
                  <tr key={cohort.cohort} data-analytics-cohort-row>
                    <th scope="row">{cohort.cohort}</th>
                    <td>
                      {copy.plural(
                        "analytics.dashboardBuilder.users",
                        cohort.size,
                      )}
                    </td>
                    {intervals.map((day) => {
                      const point = getRetentionPoint(cohort, day);
                      return (
                        <td key={`${cohort.cohort}-${day}`}>
                          <span className="gn-analytics-cohort-table__rate">
                            {formatPercent(point?.retentionRate)}
                          </span>
                          <span className="gn-analytics-cohort-table__retained">
                            {point?.retainedUsers.toLocaleString() ?? "0"}
                          </span>
                        </td>
                      );
                    })}
                    <td>{copy.formatTime(cohort.lastActivity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
